<script lang="ts" setup>
import OverlayBase from '@/components/overlays/OverlayBase.vue'
import OverlayHeaderLogo from '@/components/overlays/OverlayHeaderLogo.vue'
import SignersTable from '@/components/signature-request/SignersTable.vue'
import UpgradeToStarterPlan from '@/components/UpgradeToStarterPlan.vue'
import UpgradeToStarterPromo from '@/components/UpgradeToStarterPromo.vue'

defineProps<{
  signatureRequest: SignatureRequestData
}>()

const emit = defineEmits<{
  close: []
}>()

const { t } = useI18n()
const { mdAndDown, lgAndUp } = useDisplay()
const { track } = useAnalytics()

const { ujoInviteLoopMessageVariant } = useActiveExperiments()
const showPromoVariant = ujoInviteLoopMessageVariant.value === 'v1'

onMounted(() => {
  track(ANALYTICS_EVENTS.DOCUMENT_SIGNED_AS_INVITEE)
})

const onDismiss = () => {
  track(ANALYTICS_EVENTS.STARTER_SIGNUP_SKIPPED)
  emit('close')
}
</script>

<template>
  <overlay-base
    data-cy="overlay_signing_success_nas"
    :transition="false"
    :scroll-strategy="mdAndDown ? 'none' : 'block'"
  >
    <template #header>
      <overlay-header-logo
        url="/login"
        :floating="showPromoVariant && lgAndUp"
        :class="{ 'overlay__header--promo': showPromoVariant }"
        @redirect="onDismiss"
      />
    </template>
    <div v-if="showPromoVariant" class="overlay__split">
      <div class="overlay__result">
        <div class="overlay__result-main">
          <div class="overlay__result-content">
            <skr-heading level="1" class="overlay__title ma-0" data-cy="overlay_title">
              {{ t('sign.success.title') }}
            </skr-heading>
            <p class="h3 overlay__subtitle overlay__result-subtitle">{{ t('sign.success.nas_subtitle') }}</p>
            <div class="overlay__result-signers" data-cy="sr_signers">
              <signers-table :signature-request-data="signatureRequest" />
            </div>
          </div>
        </div>
      </div>
      <div class="overlay__promo-pane">
        <upgrade-to-starter-promo @close="emit('close')" />
      </div>
    </div>
    <v-container v-else class="pb-16">
      <div class="d-flex w-100 justify-center gap-10" :class="mdAndDown ? 'flex-column-reverse' : 'flex-row'">
        <v-col :class="mdAndDown ? 'pa-8' : 'overlay__signers pr-10'" cols="12" lg="6" xl="4">
          <div>
            <div class="text-left">
              <div class="my-4">
                <skr-heading level="1" class="overlay__title ma-0" data-cy="overlay_title">
                  {{ t('sign.success.title') }}
                </skr-heading>
              </div>
            </div>

            <div class="mb-12 text-left">
              <p class="h3 overlay__subtitle">{{ t('sign.success.nas_subtitle') }}</p>
            </div>
          </div>

          <div class="mt-16" data-cy="sr_signers">
            <signers-table :signature-request-data="signatureRequest" />
          </div>
        </v-col>
        <v-col class="py-0" :class="{ 'pl-10': !mdAndDown }" cols="12" lg="6" xl="4">
          <upgrade-to-starter-plan @close="emit('close')" />
        </v-col>
      </div>
    </v-container>
  </overlay-base>
</template>

<style lang="scss" scoped>
.overlay {
  &__title {
    font-size: $h1-font-size-mobile;
  }
  &__subtitle {
    color: $c-grey-dark;
  }
  &__signers {
    overflow-y: auto;
    height: 100vh;
    padding-top: 140px;
    padding-bottom: 140px;
  }
  &__header--promo {
    // Align the logo with the 2rem content padding (header default is 2.5rem)
    @include max-width(md) {
      &.header {
        padding-inline: 8 * $bw;
      }
    }

    // Floating (lg+) the header spans the full width above both panes; let
    // cursor events reach the promo panel underneath, except on the logo link.
    @include min-width(lg) {
      pointer-events: none;

      :deep(.skribble-logo-text) {
        display: none;
      }

      :deep(.header__link) {
        pointer-events: auto;
      }
    }
  }
  &__split {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    justify-content: center;

    @include min-width(lg) {
      flex-direction: row;
      align-items: stretch;
      height: 100vh;
      overflow: hidden;
    }
  }
  &__result {
    display: flex;
    flex-direction: column;

    @include min-width(lg) {
      flex: 0 0 55%;
      min-width: 0;
      height: 100%;
      overflow-y: auto;
    }
  }
  &__result-main {
    display: flex;
    flex: 1 0 auto;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 8 * $bw;

    @include min-width(lg) {
      padding: 12 * $bw 16 * $bw;
    }
  }
  &__result-content {
    width: 460px;
    max-width: 100%;
    text-align: left;
  }
  &__result-subtitle {
    margin: 4 * $bw 0 0;
  }
  &__result-signers {
    margin-top: 12 * $bw;
  }
  &__promo-pane {
    width: 100%;
    max-width: calc(460px + #{16 * $bw});
    margin-inline: auto;
    padding: 8 * $bw;

    @include min-width(lg) {
      flex: 0 0 45%;
      min-width: 0;
      max-width: none;
      height: 100%;
      padding: 12 * $bw;
    }
  }
}
</style>
